import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { config } from '../config';
import { shortPlace } from '../lib/format';
import { HERO_SCENES } from '../assets/hero';
import { Icon, Logo } from '../components/Icon';
import { PhotoBackdrop } from '../components/PhotoBackdrop';
import { Screen } from '../components/layout';
import { ListingCard, ListingGrid, ListingGridSkeleton } from '../components/listing';
import { CategoryGrid, LocationPickerSheet, useLocationPath } from '../components/pickers';
import { EmptyState, ErrorState, errorMessage, useToast } from '../components/ui';
import { useAuth } from '../lib/auth';
import { openExternal } from '../lib/telegram';
import { HOME_FRESH_LIMIT, HOME_FRESH_PARAMS } from '../lib/early-start';
import { useBanners, useNotifications, usePopular, usePromoted, useSearch, useUpdateMe } from '../lib/queries';

function Section({ title, link, children }: { title: string; link?: string; children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <section className="section">
      <div className="section-head">
        <h2>{title}</h2>
        {link ? (
          <Link to={link} className="link">
            {t('common.seeAll')}
          </Link>
        ) : null}
      </div>
      {children}
    </section>
  );
}

export function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { me, setMe } = useAuth();
  const toast = useToast();
  const updateMe = useUpdateMe();
  const [pickRegion, setPickRegion] = useState(false);

  const locationId = me?.settings.locationId ?? undefined;
  const locationPath = useLocationPath(locationId);
  const locationName = locationPath[locationPath.length - 1]?.name;
  // В чипе — коротко: «Нукус» вместо «город Нукус» / «Nukus shahri» (полное название — в заголовке раздела).
  const locationShort = locationName ? shortPlace(locationName) : undefined;

  // Те же параметры, что и у ранней предзагрузки (lib/early-start.ts) — иначе кэш не совпадёт.
  const fresh = useSearch(HOME_FRESH_PARAMS, true, HOME_FRESH_LIMIT);
  const local = useSearch({ sort: 'newest', locationId }, Boolean(locationId), 6);
  const popular = usePopular(locationId);
  const banners = useBanners();
  const promoted = usePromoted(locationId);
  const notifications = useNotifications();

  const freshItems = fresh.data?.pages.flatMap((p) => p.items) ?? [];
  const localItems = local.data?.pages.flatMap((p) => p.items) ?? [];
  // «Рекомендуем»: сначала VIP (продвигаемые), затем популярные — без повторов; популярные ниже уже не дублируются.
  const recommended = [...(promoted.data ?? []), ...(popular.data ?? [])].filter((x, i, a) => a.findIndex((y) => y.id === x.id) === i).slice(0, 10);

  return (
    <Screen>
      <div className="home-top">
        <div className="brand">
          <Logo size={34} />
          <span className="brand-name brand-stack">
            <span>{config.appName.replace(/\s+\S+$/, '') || config.appName}</span>
            {/\s/.test(config.appName) ? <span className="brand-sub">{config.appName.split(/\s+/).pop()}</span> : null}
          </span>
        </div>
        <div className="home-top-actions">
          <button type="button" className="region-chip" onClick={() => setPickRegion(true)} aria-label={`${t('location.label')}: ${locationName ?? t('location.all')}`}>
            <Icon name="pin" size={16} />
            <span>{locationShort ?? t('location.allShort')}</span>
          </button>
          <button type="button" className="icon-btn bell-btn" aria-label={t('profile.notifications')} onClick={() => navigate('/notifications')}>
            <Icon name="bell" size={22} />
            {notifications.data?.unread ? <span className="bell-dot" aria-hidden /> : null}
          </button>
        </div>
      </div>

      <div className="search-row">
        <button type="button" className="search-trigger" onClick={() => navigate('/search?focus=1')}>
          <Icon name="search" size={20} />
          <span>{t('search.placeholder')}</span>
        </button>
        <button type="button" className="search-filter-btn" onClick={() => navigate('/search?filters=1')} aria-label={t('search.filters')}>
          <Icon name="filter" size={20} />
        </button>
      </div>

      <Link to="/sell" className="hero">
        <PhotoBackdrop scenes={HERO_SCENES} storageKey="qm.heroScene" fallback={<HeroArt />} shadeClass="hero-shade" />
        <span className="hero-body">
          <strong className="hero-title">{t('home.heroTitle')}</strong>
          <span className="hero-text">{t('home.heroText')}</span>
          <span className="hero-btn">
            <Icon name="plus" size={18} /> {t('home.heroButton')}
          </span>
        </span>
      </Link>

      <div className="section-head home-cats-head">
        <h2>{t('categories.title')}</h2>
        <Link to="/categories" className="link">
          {t('common.seeAll')}
        </Link>
      </div>
      <CategoryGrid onSelect={(c) => navigate(`/search?categoryId=${c.id}`)} />

      {banners.data && banners.data.length > 0 ? (
        <div className="banners">
          {banners.data.map((b) => (
            <button
              type="button"
              key={b.id}
              className="banner"
              onClick={() => b.linkUrl && openExternal(b.linkUrl)}
              aria-label={b.title}
            >
              <img src={b.imageUrl} alt={b.title} loading="lazy" />
              <span className="badge badge-ad">{t('common.ad')}</span>
            </button>
          ))}
        </div>
      ) : null}

      {recommended.length > 0 ? (
        <Section title={t('home.recommended')} link="/search">
          <div className="h-scroll">
            {recommended.map((item) => (
              <div key={item.id} className="h-scroll-item">
                <ListingCard item={item} />
              </div>
            ))}
          </div>
          {promoted.data?.length ? <p className="tiny muted">{t('home.promotedNote')}</p> : null}
        </Section>
      ) : null}

      {locationId ? (
        <Section title={t('home.inYourPlace', { place: locationShort ?? '' })} link={`/search?locationId=${locationId}`}>
          {local.isLoading ? (
            <ListingGridSkeleton count={4} />
          ) : local.error ? (
            <ErrorState error={local.error} onRetry={() => void local.refetch()} />
          ) : localItems.length ? (
            <ListingGrid items={localItems} />
          ) : (
            <EmptyState icon="pin" title={t('home.noLocal')} />
          )}
        </Section>
      ) : null}

      <Section title={t('home.fresh')} link="/search">
        {fresh.isLoading ? (
          <ListingGridSkeleton />
        ) : fresh.error ? (
          <ErrorState error={fresh.error} onRetry={() => void fresh.refetch()} />
        ) : freshItems.length ? (
          <ListingGrid items={freshItems} />
        ) : (
          <EmptyState icon="grid" title={t('home.empty')} text={t('home.emptyText')} />
        )}
      </Section>

      <LocationPickerSheet
        open={pickRegion}
        onClose={() => setPickRegion(false)}
        selectedId={locationId}
        onSelect={(l) =>
          updateMe.mutate(
            { path: '/users/me/settings', method: 'PATCH', json: { locationId: l?.id ?? null } },
            { onSuccess: setMe, onError: (e) => toast(errorMessage(t, e), 'error') },
          )
        }
      />
    </Screen>
  );
}

/** Фон баннера: закат над пустыней и древней крепостью на холме (как Аяз-кала). Рисунок, без фото. */
function HeroArt() {
  return (
    <svg className="hero-art" viewBox="0 0 360 160" preserveAspectRatio="xMidYMid slice" aria-hidden>
      <defs>
        <linearGradient id="hero-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#10302b" />
          <stop offset=".5" stopColor="#5b5a3c" />
          <stop offset=".8" stopColor="#d98a3d" />
          <stop offset="1" stopColor="#f2b35c" />
        </linearGradient>
        <radialGradient id="hero-sun" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(300 100) scale(70)">
          <stop offset="0" stopColor="#ffd88a" stopOpacity=".95" />
          <stop offset=".35" stopColor="#ffc163" stopOpacity=".55" />
          <stop offset="1" stopColor="#ffc163" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="hero-fade" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#0b1d1a" stopOpacity=".95" />
          <stop offset=".5" stopColor="#0b1d1a" stopOpacity=".6" />
          <stop offset=".8" stopColor="#0b1d1a" stopOpacity=".05" />
          <stop offset="1" stopColor="#0b1d1a" stopOpacity="0" />
        </linearGradient>
      </defs>
      <rect width="360" height="160" fill="url(#hero-sky)" />
      <circle cx="300" cy="100" r="70" fill="url(#hero-sun)" />
      <circle cx="300" cy="100" r="15" fill="#ffe2a3" />
      {/* холм и крепость с башнями */}
      <path d="M178 132c18-22 40-34 70-36h70c26 2 40 14 48 36z" fill="#6b4a2c" />
      <path
        d="M214 100V78h6v-7h8v7h10V66h7v-6h8v6h7v12h12V70h7v-6h8v6h7v8h10v-8h7v-6h8v6h6v30z"
        fill="#3a2a1d"
      />
      <path d="M233 100v-12h6v12zm33 0v-14h6v14zm34 0v-10h5v10z" fill="#1d1510" opacity=".7" />
      {/* барханы */}
      <path d="M0 136c46-16 92-18 140-8s96 12 140 0 60-12 80-8v40H0z" fill="#9a6535" />
      <path d="M0 148c60-12 120-10 180-2s120 8 180-4v18H0z" fill="#7a4d29" />
      <rect width="360" height="160" fill="url(#hero-fade)" />
    </svg>
  );
}
